import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Handshake, Wallet, ArrowRight, Check, Clock, Shield, Zap, Phone, MessageCircle, Mail,
  Upload, Percent, ArrowUpRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { getYouTubeId } from "@/lib/videoUrl";

const HERO = "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/815157341/5576012257.jpg";

export default function Sell() {
  const { t, lang } = useLang();
  const [site, setSite] = useState({});
  const [tab, setTab] = useState("buyout"); // buyout | consignment
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    brand: "", model: "", year: "", mileage: "",
    message: "",
  });

  useEffect(() => { api.get("/site").then((r) => setSite(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const heroYtId = getYouTubeId(site.hero_video_url);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const subject = tab === "buyout" ? "Demande de rachat" : "Demande de dépôt-vente";
    const messageBody = `Formule souhaitée : ${tab === "buyout" ? "RACHAT DIRECT" : "DÉPÔT-VENTE"}\n\n` +
      `Véhicule : ${form.brand} ${form.model} (${form.year})\n` +
      `Kilométrage : ${form.mileage} km\n\n` +
      `Message :\n${form.message || "-"}`;

    try {
      await api.post("/contact", {
        name: form.name, email: form.email, phone: form.phone,
        subject, message: messageBody, request_type: "sell",
      });
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm({ name: "", email: "", phone: "", brand: "", model: "", year: "", mileage: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const waHref = site.whatsapp ? `https://wa.me/${site.whatsapp.replace(/[^\d]/g,"")}?text=${encodeURIComponent("Bonjour KV, je souhaite " + (tab === "buyout" ? "vendre" : "mettre en dépôt") + " mon véhicule.")}` : "#";
  const telHref = site.phone ? `tel:${site.phone.replace(/\s+/g,"")}` : "#";

  const buyout = {
    fr: {
      kicker: "Solution 1",
      title: "Nous rachetons votre véhicule",
      lead: "Un seul interlocuteur. Un check-up complet. Un prix ferme et rapide, dans les meilleures conditions du marché.",
      bullets: [
        { icon: Zap, label: "Estimation sous 24-48 h" },
        { icon: Wallet, label: "Paiement rapide, sécurisé" },
        { icon: Shield, label: "Aucune démarche administrative pour vous" },
        { icon: Clock, label: "Pas de rendez-vous sans suite" },
      ],
      ideal: "Idéal si vous voulez vendre vite, sans stress, sans négociation infinie.",
    },
    nl: {
      kicker: "Oplossing 1",
      title: "Wij kopen uw wagen",
      lead: "Eén aanspreekpunt. Volledige check-up. Een vaste en snelle prijs onder de beste marktvoorwaarden.",
      bullets: [
        { icon: Zap, label: "Taxatie binnen 24-48 u" },
        { icon: Wallet, label: "Snelle en veilige betaling" },
        { icon: Shield, label: "Geen administratieve rompslomp" },
        { icon: Clock, label: "Geen afspraken zonder gevolg" },
      ],
      ideal: "Ideaal als u snel wilt verkopen, zonder stress en zonder eindeloos onderhandelen.",
    },
    en: {
      kicker: "Option 1",
      title: "We buy your car",
      lead: "One dedicated contact. Full check-up. A firm, fast offer at the best market conditions.",
      bullets: [
        { icon: Zap, label: "Valuation within 24-48 h" },
        { icon: Wallet, label: "Fast, secure payment" },
        { icon: Shield, label: "Zero paperwork on your side" },
        { icon: Clock, label: "No time-wasting meetings" },
      ],
      ideal: "Ideal if you want to sell quickly, without stress or endless negotiation.",
    },
  }[lang] || undefined;

  const consign = {
    fr: {
      kicker: "Solution 2",
      title: "Dépôt-vente premium",
      lead: "Nous prenons votre voiture en dépôt dans nos locaux à Rhode-Saint-Genèse. Nous estimons ensemble le prix juste et vendons rapidement.",
      bullets: [
        { icon: Percent, label: "Prix optimisé pour vous" },
        { icon: Handshake, label: "Vitrine premium & réseau ciblé" },
        { icon: Upload, label: "Mise en scène professionnelle" },
        { icon: Shield, label: "Contrat clair, transparence totale" },
      ],
      ideal: "Idéal si vous cherchez à maximiser le prix de vente et laissez-nous gérer.",
    },
    nl: {
      kicker: "Oplossing 2",
      title: "Premium consignatieverkoop",
      lead: "Wij nemen uw wagen in consignatie in onze locatie in Sint-Genesius-Rode. Samen bepalen we de juiste prijs en verkopen snel.",
      bullets: [
        { icon: Percent, label: "Geoptimaliseerde prijs" },
        { icon: Handshake, label: "Premium showroom & gericht netwerk" },
        { icon: Upload, label: "Professionele presentatie" },
        { icon: Shield, label: "Duidelijk contract, volledige transparantie" },
      ],
      ideal: "Ideaal als u de verkoopprijs wilt maximaliseren en het beheer aan ons overlaat.",
    },
    en: {
      kicker: "Option 2",
      title: "Premium consignment sale",
      lead: "We take your car in consignment at our Rhode-Saint-Genèse showroom. We agree on the fair price together and sell fast.",
      bullets: [
        { icon: Percent, label: "Optimised sale price" },
        { icon: Handshake, label: "Premium showroom & targeted network" },
        { icon: Upload, label: "Professional staging" },
        { icon: Shield, label: "Clear contract, full transparency" },
      ],
      ideal: "Ideal if you want to maximise the sale price while we handle everything.",
    },
  }[lang] || undefined;

  const active = tab === "buyout" ? buyout : consign;

  return (
    <div data-testid="sell-page" className="bg-[#0D0D0D] min-h-screen">
      <SEO title="Vendre / Reprise · Rachat direct & Dépôt-vente" description="Vendez votre véhicule de prestige sereinement : rachat direct sous 24-48h ou dépôt-vente premium dans notre showroom de Rhode-Saint-Genèse. Un seul interlocuteur." />
      {/* HERO */}
      <section className="relative min-h-[75vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {heroYtId ? (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${heroYtId}?autoplay=1&mute=1&loop=1&playlist=${heroYtId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`}
                title="Vidéo showroom KV International"
                allow="autoplay; encrypted-media"
                data-testid="sell-hero-video"
                className="absolute top-1/2 left-1/2 w-[max(100%,177.78vh)] h-[max(100%,56.25vw)] -translate-x-1/2 -translate-y-1/2 border-0"
              />
            </div>
          ) : site.hero_video_url ? (
            <video autoPlay muted loop playsInline poster={HERO} className="w-full h-full object-cover img-fade" data-testid="sell-hero-video">
              <source src={site.hero_video_url} type="video/mp4" />
            </video>
          ) : (
            <img src={HERO} alt="Vendre votre Porsche — Rachat direct & dépôt-vente KV International" fetchpriority="high" decoding="async" className="w-full h-full object-cover kenburns img-fade" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/80 to-[#0D0D0D]/50" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 pt-32 w-full">
          <p className="text-xs uppercase tracking-[0.4em] text-[#C8A75A] mb-4 fade-up">Vendre · Reprise · Dépôt-vente · Showroom Rhode-Saint-Genèse</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light text-white max-w-4xl fade-up fade-up-delay-1">
            Vendre votre véhicule ?
          </h1>
          <p className="text-white/70 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed fade-up fade-up-delay-2">
            Vous n'avez pas le temps pour des rendez-vous sans suite ni la charge administrative. Un seul interlocuteur, deux formules — au choix.
          </p>
        </div>
      </section>

      {/* OFFERS TABS */}
      <section className="relative -mt-14 z-20 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { key: "buyout", label: buyout.title, icon: Wallet, sub: "Rachat direct" },
            { key: "consignment", label: consign.title, icon: Handshake, sub: "Dépôt-vente" },
          ].map((c) => (
            <button
              key={c.key}
              data-testid={`tab-${c.key}`}
              onClick={() => setTab(c.key)}
              className={`text-left group relative overflow-hidden rounded-md p-8 transition-all duration-500 border ${
                tab === c.key
                  ? "bg-[#C8A75A] border-[#C8A75A] text-black shadow-[0_20px_60px_rgba(200,167,90,0.25)]"
                  : "bg-[#1E1E1E] border-white/10 text-white hover:border-[#C8A75A]/40"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <span className={`w-12 h-12 rounded-md flex items-center justify-center ${tab === c.key ? "bg-black/10" : "bg-[#C8A75A]/10"}`}>
                    <c.icon className={`w-6 h-6 ${tab === c.key ? "text-black" : "text-[#C8A75A]"}`} strokeWidth={1.4} />
                  </span>
                  <div>
                    <p className={`text-[10px] uppercase tracking-[0.3em] ${tab === c.key ? "text-black/60" : "text-[#C8A75A]"}`}>{c.sub}</p>
                    <p className="font-display text-xl md:text-2xl mt-1">{c.label}</p>
                  </div>
                </div>
                <ArrowUpRight className={`w-5 h-5 mt-1 transition-transform ${tab === c.key ? "text-black" : "text-white/40"} group-hover:translate-x-0.5 group-hover:-translate-y-0.5`} />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
          {/* Left: benefits of active tab */}
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{active.kicker}</p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-4">{active.title}</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">{active.lead}</p>

            <ul className="space-y-4 mb-10">
              {active.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-white/5 pb-4">
                  <b.icon className="w-5 h-5 text-[#C8A75A] mt-0.5 flex-shrink-0" strokeWidth={1.3} />
                  <span className="text-white/85">{b.label}</span>
                </li>
              ))}
            </ul>

            <div className="bg-[#1E1E1E] border-l-2 border-[#C8A75A] p-6 rounded-r-md">
              <p className="text-white/80 italic">{active.ideal}</p>
            </div>

            {/* Quick channels */}
            <div className="mt-10 grid grid-cols-2 gap-3">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="sell-wa" className="flex items-center justify-center gap-2 bg-[#25D366] text-black hover:bg-white transition-colors px-4 py-4 rounded-md text-xs uppercase tracking-widest font-medium">
                <MessageCircle className="w-4 h-4" /> WhatsApp 24/24
              </a>
              <a href={telHref} data-testid="sell-tel" className="flex items-center justify-center gap-2 border border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black transition-colors px-4 py-4 rounded-md text-xs uppercase tracking-widest">
                <Phone className="w-4 h-4" /> {site.phone || "Appeler"}
              </a>
            </div>
          </div>

          {/* Right: form — 4 champs véhicule + 1 contact minimal */}
          <form onSubmit={submit} data-testid="sell-form" className="bg-[#1E1E1E] border border-white/10 rounded-md p-8 lg:p-10 lg:sticky lg:top-32">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C8A75A] mb-2">
              {tab === "buyout" ? "Demander mon offre de rachat" : "Proposer en dépôt-vente"}
            </p>
            <h3 className="font-display text-xl md:text-2xl text-white mb-2 leading-tight">Estimation gratuite en 4 champs</h3>
            <p className="text-white/50 text-sm mb-6">On vous rappelle dans la journée.</p>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Marque" req>
                <Input data-testid="sell-brand" required value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Porsche" className="bg-[#0D0D0D] border-white/10 h-11 text-white" />
              </Field>
              <Field label="Modèle" req>
                <Input data-testid="sell-model" required value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="911 Carrera" className="bg-[#0D0D0D] border-white/10 h-11 text-white" />
              </Field>
              <Field label="Année" req>
                <Input data-testid="sell-year" type="number" required value={form.year} onChange={(e) => set("year", e.target.value)} placeholder="2018" className="bg-[#0D0D0D] border-white/10 h-11 text-white" />
              </Field>
              <Field label="Kilométrage" req>
                <Input data-testid="sell-km" type="number" required value={form.mileage} onChange={(e) => set("mileage", e.target.value)} placeholder="45 000" className="bg-[#0D0D0D] border-white/10 h-11 text-white" />
              </Field>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10 grid md:grid-cols-2 gap-3">
              <Field label="Votre nom" req>
                <Input data-testid="sell-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0D0D0D] border-white/10 h-11 text-white" />
              </Field>
              <Field label="Téléphone" req>
                <Input data-testid="sell-phone" type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0D0D0D] border-white/10 h-11 text-white" />
              </Field>
              <Field label="Email">
                <Input data-testid="sell-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0D0D0D] border-white/10 h-11 text-white md:col-span-2" />
              </Field>
            </div>

            <Button type="submit" disabled={sending} data-testid="sell-submit" className="mt-6 bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs px-8 py-6 rounded-md w-full">
              {sending ? "…" : (tab === "buyout" ? "Recevoir mon offre" : "Proposer mon véhicule")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <p className="text-white/40 text-xs mt-4 text-center">
              Vos données restent confidentielles · Aucun engagement · Réponse dans la journée
            </p>
          </form>
        </div>
      </section>

      {/* HOW IT WORKS timeline */}
      <section className="py-32 bg-[#1E1E1E]/30 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Processus</p>
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-light text-white mb-14">4 étapes, aucune surprise.</h3>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Vous nous contactez", d: "Formulaire, WhatsApp, appel — 24/24h." },
              { n: "02", t: "Estimation", d: "Nous étudions votre dossier et revenons sous 24-48 h." },
              { n: "03", t: "Showroom Rhode-Saint-Genèse", d: "Check-up rapide et évaluation en présentiel, sur rendez-vous." },
              { n: "04", t: "Transaction", d: "Rachat ou signature du dépôt — clair et transparent." },
            ].map((s, i) => (
              <div key={i} className="bg-[#0D0D0D] border border-white/10 rounded-md p-8 relative">
                <span className="font-display text-6xl text-[#C8A75A]/25 leading-none">{s.n}</span>
                <p className="text-white text-lg mt-4 font-medium">{s.t}</p>
                <p className="text-white/60 text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          {/* Showroom card */}
          <div className="mt-12 bg-[#0D0D0D] border border-[#C8A75A]/30 rounded-md p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A] mb-2">Showroom d'exposition</p>
              <p className="font-display text-xl md:text-2xl text-white mb-2 leading-tight">Chaussée de Waterloo 242 · 1640 Rhode-Saint-Genèse</p>
              <p className="text-white/60 text-sm">Réception uniquement sur rendez-vous · +32 486 52 19 06 · 7j/7</p>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Chauss%C3%A9e+de+Waterloo+242+1640+Rhode-Saint-Gen%C3%A8se"
              target="_blank" rel="noreferrer"
              data-testid="sell-showroom-directions"
              className="bg-[#C8A75A] text-black hover:bg-white transition-colors px-6 py-3 rounded-md text-xs uppercase tracking-widest inline-flex items-center gap-2 font-medium whitespace-nowrap"
            >
              Itinéraire <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">
            Une question avant de démarrer ?
          </h2>
          <p className="text-white/60 text-lg mb-10">Notre équipe vous répond 24/24h. Uniquement sur rendez-vous en showroom.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={waHref} target="_blank" rel="noreferrer" className="bg-[#25D366] text-black hover:bg-white transition-colors px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href={telHref} className="bg-[#C8A75A] text-black hover:bg-white transition-colors px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> {site.phone}
            </a>
            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/5 transition-colors px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> Formulaire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, req, children }) {
  return (
    <div className="mt-3">
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">
        {label}{req && <span className="text-[#C8A75A]"> *</span>}
      </label>
      {children}
    </div>
  );
}
